import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LocationDropdown } from './LocationDropdown';
import EmployeePhotoUpload from './EmployeePhotoUpload';
import { wilayahService } from '../services/wilayahService';
import type { Employee, EmploymentStatus, EmergencyContact, CreateEmployeeRequest, UpdateEmployeeRequest } from '../types';

interface EmployeeFormProps {
  mode: 'create' | 'edit';
  employeeId?: string;
  onSuccess?: () => void;
}

interface FormData {
  NIP: string;
  Nama: string;
  Jabatan: string;
  Status_Pekerjaan: EmploymentStatus;
  Foto: string;
  No_Telp: string;
  Tanggal_Lahir: string;
  Jenis_Kelamin: string;
  Provinsi: string;
  Kota: string;
  Kecamatan: string;
  KTP: string;
  Status_Kawin: string;
  Kontak_Darurat: EmergencyContact;
}

interface FormErrors {
  [key: string]: string;
}

const EMPLOYMENT_STATUS_OPTIONS: { value: EmploymentStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'konseling', label: 'Konseling' },
  { value: 'sp1', label: 'SP1' },
  { value: 'sp2', label: 'SP2' },
  { value: 'sp3', label: 'SP3' },
  { value: 'inactive', label: 'Inactive' },
];

export default function EmployeeForm({ mode, employeeId, onSuccess }: EmployeeFormProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingEmployee, setLoadingEmployee] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});

  const [formData, setFormData] = useState<FormData>({
    NIP: '',
    Nama: '',
    Jabatan: '',
    Status_Pekerjaan: 'active',
    Foto: '',
    No_Telp: '',
    Tanggal_Lahir: '',
    Jenis_Kelamin: '',
    Provinsi: '',
    Kota: '',
    Kecamatan: '',
    KTP: '',
    Status_Kawin: '',
    Kontak_Darurat: {
      name: '',
      relationship: '',
      phone: '',
      address: '',
    },
  });

  // Location state for cascading dropdowns
  const [selectedProvinceId, setSelectedProvinceId] = useState<string>('');
  const [selectedCityId, setSelectedCityId] = useState<string>('');
  const [selectedDistrictId, setSelectedDistrictId] = useState<string>('');

  // Load employee data when editing
  useEffect(() => {
    if (mode === 'edit' && employeeId) {
      loadEmployee();
    }
  }, [mode, employeeId]);

  const loadEmployee = async () => {
    if (!employeeId) return;

    setLoadingEmployee(true);
    try {
      const employee = await fetchEmployeeById(employeeId);
      
      // Populate form data
      setFormData({
        NIP: employee.NIP || '',
        Nama: employee.Nama || '',
        Jabatan: employee.Jabatan || '',
        Status_Pekerjaan: employee.Status_Pekerjaan || 'active',
        Foto: employee.Foto || '',
        No_Telp: employee.No_Telp || '',
        Tanggal_Lahir: employee.Tanggal_Lahir ? employee.Tanggal_Lahir.split('T')[0] : '',
        Jenis_Kelamin: employee.Jenis_Kelamin || '',
        Provinsi: employee.Provinsi || '',
        Kota: employee.Kota || '',
        Kecamatan: employee.Kecamatan || '',
        KTP: employee.KTP || '',
        Status_Kawin: employee.Status_Kawin || '',
        Kontak_Darurat: employee.Kontak_Darurat || {
          name: '',
          relationship: '',
          phone: '',
          address: '',
        },
      });

      // Set photo preview
      if (employee.Foto) {
        // Photo will be set via formData.Foto
      }

      // Load location IDs from names
      if (employee.Provinsi) {
        try {
          const provinces = await wilayahService.getProvinces();
          const province = provinces.find(p => p.name === employee.Provinsi);
          if (province) {
            setSelectedProvinceId(province.id);
            
            // Load cities for this province
            if (employee.Kota) {
              const cities = await wilayahService.getCities(province.id);
              const city = cities.find(c => c.name === employee.Kota);
              if (city) {
                setSelectedCityId(city.id);
                
                // Load districts for this city
                if (employee.Kecamatan) {
                  const districts = await wilayahService.getDistricts(city.id);
                  const district = districts.find(d => d.name === employee.Kecamatan);
                  if (district) {
                    setSelectedDistrictId(district.id);
                  }
                }
              }
            }
          }
        } catch (err) {
          console.error('Error loading location data:', err);
          // Continue without location IDs - they'll be empty but form data is populated
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load employee data');
    } finally {
      setLoadingEmployee(false);
    }
  };

  // Fetch employee by ID (placeholder - will be implemented in API)
  const fetchEmployeeById = async (id: string): Promise<Employee> => {
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3031/api'}/employees/${id}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch employee');
    }

    const result = await response.json();
    return result.data;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear field error when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleEmergencyContactChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      Kontak_Darurat: {
        ...prev.Kontak_Darurat,
        [name]: value,
      },
    }));
  };

  const handlePhotoChange = (base64: string | null) => {
    setFormData(prev => ({
      ...prev,
      Foto: base64 || '',
    }));
  };

  const handleLocationChange = (level: 'province' | 'city' | 'district', id: string, name: string) => {
    if (level === 'province') {
      setSelectedProvinceId(id);
      setFormData(prev => ({
        ...prev,
        Provinsi: name,
        Kota: '', // Clear dependent fields
        Kecamatan: '',
      }));
      setSelectedCityId(''); // Clear city selection
      setSelectedDistrictId(''); // Clear district selection
    } else if (level === 'city') {
      setSelectedCityId(id);
      setFormData(prev => ({
        ...prev,
        Kota: name,
        Kecamatan: '', // Clear dependent field
      }));
      setSelectedDistrictId(''); // Clear district selection
    } else if (level === 'district') {
      setSelectedDistrictId(id);
      setFormData(prev => ({
        ...prev,
        Kecamatan: name,
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Required fields
    if (!formData.Nama.trim()) {
      newErrors.Nama = 'Name is required';
    }

    if (!formData.Status_Pekerjaan) {
      newErrors.Status_Pekerjaan = 'Employment status is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      setError('Please fix the errors in the form');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload: CreateEmployeeRequest | UpdateEmployeeRequest = {
        NIP: formData.NIP || undefined,
        Nama: formData.Nama,
        Jabatan: formData.Jabatan || undefined,
        Status_Pekerjaan: formData.Status_Pekerjaan,
        Foto: formData.Foto || undefined,
        No_Telp: formData.No_Telp || undefined,
        Tanggal_Lahir: formData.Tanggal_Lahir || undefined,
        Jenis_Kelamin: formData.Jenis_Kelamin || undefined,
        Provinsi: formData.Provinsi || undefined,
        Kota: formData.Kota || undefined,
        Kecamatan: formData.Kecamatan || undefined,
        KTP: formData.KTP || undefined,
        Status_Kawin: formData.Status_Kawin || undefined,
        Kontak_Darurat: formData.Kontak_Darurat.name ? formData.Kontak_Darurat : undefined,
      };

      if (mode === 'create') {
        await createEmployee(payload as CreateEmployeeRequest);
      } else {
        await updateEmployee(employeeId!, payload as UpdateEmployeeRequest);
      }

      // Success
      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/employees');
      }
    } catch (err: any) {
      console.error('Form submission error:', err);
      
      // Handle field-specific errors from API
      if (err.details && typeof err.details === 'object') {
        setErrors(err.details);
        setError('Please fix the errors in the form');
      } else {
        setError(err.message || 'Failed to save employee');
      }
    } finally {
      setLoading(false);
    }
  };

  // API calls (placeholder - will use api utility)
  const createEmployee = async (data: CreateEmployeeRequest) => {
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3031/api'}/employees`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw errorData;
    }

    return response.json();
  };

  const updateEmployee = async (id: string, data: UpdateEmployeeRequest) => {
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3031/api'}/employees/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw errorData;
    }

    return response.json();
  };

  if (loadingEmployee) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading employee data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:px-6 pt-20">
      <div className="bg-white rounded-lg shadow-lg">
        <div className="p-4 md:p-6 border-b rounded-lg bg-black">
          <h2 className="text-2xl font-semibold text-white">
            {mode === 'create' ? 'Create New Employee' : 'Edit Employee'}
          </h2>
          <p className="text-blue-100 text-sm mt-1">
            {mode === 'create' ? 'Fill in the information below to add a new employee' : 'Update employee information'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-4 md:p-6">
          {/* Global Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Personal Information Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Personal Information</h3>

                {/* NIP */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    NIP (Nomor Induk Pegawai)
                  </label>
                  <input
                    type="text"
                    name="NIP"
                    value={formData.NIP}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.NIP ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter employee ID number"
                  />
                  {errors.NIP && <p className="mt-1 text-sm text-red-600">{errors.NIP}</p>}
                </div>

                {/* Name - Required */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="Nama"
                    value={formData.Nama}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.Nama ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter employee name"
                  />
                  {errors.Nama && <p className="mt-1 text-sm text-red-600">{errors.Nama}</p>}
                </div>

                {/* Photo Upload */}
                <div>
                  <EmployeePhotoUpload
                    currentPhoto={formData.Foto}
                    onPhotoChange={handlePhotoChange}
                    disabled={loading}
                  />
                </div>

                {/* KTP */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    National ID (KTP)
                  </label>
                  <input
                    type="text"
                    name="KTP"
                    value={formData.KTP}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter KTP number"
                  />
                </div>

                {/* Birth Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Birth Date
                  </label>
                  <input
                    type="date"
                    name="Tanggal_Lahir"
                    value={formData.Tanggal_Lahir}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Gender */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Gender
                  </label>
                  <select
                    name="Jenis_Kelamin"
                    value={formData.Jenis_Kelamin}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select gender</option>
                    <option value="Laki-laki">Male</option>
                    <option value="Perempuan">Female</option>
                  </select>
                </div>

                {/* Marital Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Marital Status
                  </label>
                  <select
                    name="Status_Kawin"
                    value={formData.Status_Kawin}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select marital status</option>
                    <option value="Belum Kawin">Single</option>
                    <option value="Kawin">Married</option>
                    <option value="Cerai">Divorced</option>
                  </select>
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="No_Telp"
                    value={formData.No_Telp}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter phone number"
                  />
                </div>
              </div>

              {/* Employment Information Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Employment Information</h3>

                {/* Job Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Job Title
                  </label>
                  <input
                    type="text"
                    name="Jabatan"
                    value={formData.Jabatan}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter job title"
                  />
                </div>

                {/* Employment Status - Required */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employment Status <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="Status_Pekerjaan"
                    value={formData.Status_Pekerjaan}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.Status_Pekerjaan ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    {EMPLOYMENT_STATUS_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {errors.Status_Pekerjaan && <p className="mt-1 text-sm text-red-600">{errors.Status_Pekerjaan}</p>}
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Location Information Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Location Information</h3>

                {/* Province */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Province
                  </label>
                  <LocationDropdown
                    level="province"
                    value={selectedProvinceId}
                    onChange={(id, name) => handleLocationChange('province', id, name)}
                  />
                </div>

                {/* City */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City/Regency
                  </label>
                  <LocationDropdown
                    level="city"
                    parentId={selectedProvinceId}
                    value={selectedCityId}
                    onChange={(id, name) => handleLocationChange('city', id, name)}
                    disabled={!selectedProvinceId}
                  />
                </div>

                {/* District */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    District
                  </label>
                  <LocationDropdown
                    level="district"
                    parentId={selectedCityId}
                    value={selectedDistrictId}
                    onChange={(id, name) => handleLocationChange('district', id, name)}
                    disabled={!selectedCityId}
                  />
                </div>
              </div>

              {/* Emergency Contact Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Emergency Contact</h3>

                {/* Emergency Contact Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.Kontak_Darurat.name}
                    onChange={handleEmergencyContactChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter emergency contact name"
                  />
                </div>

                {/* Relationship */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Relationship
                  </label>
                  <input
                    type="text"
                    name="relationship"
                    value={formData.Kontak_Darurat.relationship}
                    onChange={handleEmergencyContactChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Spouse, Parent, Sibling"
                  />
                </div>

                {/* Emergency Contact Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Phone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.Kontak_Darurat.phone}
                    onChange={handleEmergencyContactChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter emergency contact phone"
                  />
                </div>

                {/* Emergency Contact Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Address
                  </label>
                  <textarea
                    name="address"
                    value={formData.Kontak_Darurat.address}
                    onChange={handleEmergencyContactChange}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter emergency contact address"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions - Full Width */}
          <div className="flex gap-3 pt-6 mt-6 border-t">
            <button
              type="button"
              onClick={() => navigate('/employees')}
              className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Saving...' : mode === 'create' ? 'Create Employee' : 'Update Employee'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
